<!--
  Artwork with the three states artwork actually has: not loaded, loaded, and
  "metahub has nothing for this id". The third is common enough on new releases
  that a broken-image glyph would be a visible defect, so the fallback is a
  typographic plate built from the title.

  THE PLATE IS THE FLOOR, NOT A BRANCH. It used to be an `{:else}` reached only
  after every source in the chain had fired `error` — which never happened for a
  poster below the fold, because `loading="lazy"` means an image that is never
  scrolled to is never requested and so never fails. A title with no artwork
  rendered as an empty rounded rectangle. Now the initials are always underneath
  and the image simply covers them when it arrives.

  Nothing here sets crossorigin: images.metahub.space sends neither ACAO nor CORP,
  so the artwork must load as an ordinary no-CORS image element. (Same reason
  COOP/COEP are absent from vite.config.ts.)
-->
<script lang="ts">
  import { sweepGate } from './sweep-gate';

  interface Props {
    src?: string | undefined;
    /** Tried in order when `src` fails. */
    fallbacks?: string[];
    alt: string;
    /** Text plate shown when every source fails. Defaults to `alt`. */
    label?: string;
    /** CSS aspect-ratio, e.g. '2 / 3'. */
    ratio?: string;
    /**
     * Caps the box height, cropping the image rather than letting `ratio` decide.
     * A 16:9 backdrop across a 1440px window is 810px tall, which pushes the page's
     * primary action below the fold; this keeps the crop and loses the height.
     */
    maxHeight?: string | undefined;
    /** Eager for the first screenful, lazy below the fold. */
    eager?: boolean;
    rounded?: boolean;
    class?: string;
    /**
     * Responsive variants (added by the CAST lane, additive): Commons only serves
     * fixed thumbnail buckets, so a face offers "…120 120w, …250 250w" and the
     * browser picks by DPR. Applied to the primary `src` only — a fallback that
     * fires has already proven this file difficult, so it loads plainly.
     */
    srcset?: string | undefined;
    sizes?: string | undefined;
  }

  let {
    src,
    fallbacks = [],
    alt,
    label,
    ratio = '2 / 3',
    maxHeight,
    eager = false,
    rounded = true,
    class: klass = '',
    srcset,
    sizes
  }: Props = $props();

  const chain = $derived([src, ...fallbacks].filter((x): x is string => !!x));

  let attempt = $state(0);
  let loaded = $state(false);

  // A new subject resets the state machine; without this, scrolling a recycled
  // row would keep the previous image's "loaded" and flash the wrong art.
  let subject = $state('');
  $effect(() => {
    const key = chain.join('|');
    if (key !== subject) {
      subject = key;
      attempt = 0;
      loaded = false;
    }
  });

  const current = $derived(chain[attempt]);
  const exhausted = $derived(chain.length === 0 || attempt >= chain.length);

  /** Two-letter plate: "The Matrix" → "TM", "Se7en" → "SE". */
  const initials = $derived(
    (label ?? alt)
      .replace(/^(the|a|an)\s+/i, '')
      .split(/[\s:–-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join('')
  );
</script>

<div
  class="art {klass}"
  class:rounded
  style:aspect-ratio={ratio}
  style:max-height={maxHeight}
  data-loaded={loaded}
  data-pending={!exhausted && !!current}
  use:sweepGate
>
  <div class="plate" aria-hidden="true">{initials || '—'}</div>
  {#if !exhausted && current}
    <img
      src={current}
      srcset={attempt === 0 && current === src ? srcset : undefined}
      sizes={attempt === 0 && current === src ? sizes : undefined}
      {alt}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      draggable="false"
      onload={() => (loaded = true)}
      onerror={() => {
        attempt += 1;
      }}
    />
  {/if}
</div>

<style>
  .art {
    position: relative;
    overflow: hidden;
    background: var(--surface-2);
    width: 100%;
    container-type: inline-size;
  }
  .rounded {
    border-radius: var(--radius);
  }

  /* The shimmer is the loading state; it stops the moment the image lands, and
     never runs over a plate that is all there is going to be. `lit` gates it
     to artwork on or near the glass (`sweep-gate.ts`): a `loading="lazy"`
     image below the fold is never fetched, so `pending` stays true for every
     unseen poster on the page — measured, 55 of these were still sweeping
     every frame on a fully-loaded Home, for nobody. */
  .art.lit[data-loaded='false'][data-pending='true']::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(100deg, transparent 20%, rgb(255 255 255 / 0.045) 42%, transparent 64%);
    background-size: 240% 100%;
    animation: sweep 1.4s var(--ease) infinite;
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    transition: opacity 260ms var(--ease);
  }
  .art[data-loaded='true'] img {
    opacity: 1;
  }

  .plate {
    position: absolute;
    inset: 0;
    display: grid;
    place-content: center;
    background: var(--surface-2);
    color: var(--text-faint);
    font-size: clamp(0.95rem, 22cqw, 2.6rem);
    font-weight: 600;
    letter-spacing: 0.06em;
  }

  @keyframes sweep {
    from {
      background-position: 140% 0;
    }
    to {
      background-position: -60% 0;
    }
  }
</style>
